import { memo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import Checkbox from "components/v2/Checkbox/Checkbox";
import Input from "components/v2/Input/Input";
import PackingListCell from "@RequestFormV2/sections/PackingList/components/shared/PackingListCell";
import PackingListSelect from "@RequestFormV2/sections/PackingList/components/shared/PackingListSelect";
import PackingListStopSelect from "@RequestFormV2/sections/PackingList/components/shared/PackingListStopSelect";
import PackingListTableRow from "@RequestFormV2/sections/PackingList/components/shared/PackingListTableRow";
import DangerousGoodsSelect from "@RequestFormV2/sections/PackingList/components/shared/DangerousGoodsSelect";
import TemperatureSelect from "@RequestFormV2/sections/PackingList/components/shared/TemperatureSelect";
import WeightUnitSelect from "@RequestFormV2/sections/PackingList/components/shared/WeightUnitSelect";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { CONTAINER_COLUMN_WIDTHS } from "@RequestFormV2/sections/PackingList/components/shared/packingListColumnWidths";
import { usePackingList } from "@RequestFormV2/sections/PackingList/context";
import { useRequestFormReviewValidation } from "@RequestFormV2/context/reviewValidation";
import { usePackingListRowReactiveClear } from "@RequestFormV2/sections/PackingList/hooks/usePackingListRowReactiveClear";
import PackingListRequiredHint from "@RequestFormV2/sections/PackingList/components/shared/PackingListRequiredHint";
import { getPackingListFieldPath } from "@RequestFormV2/sections/PackingList/utils/fieldPaths";
import { getPackingListRequiredHintPaths } from "@RequestFormV2/sections/PackingList/utils/getPackingListRequiredHintPaths";
import { getPackingListWorkflowColumnFlags } from "@RequestFormV2/sections/PackingList/utils/packingListWorkflowFlags";

type ContainerTableRowProps = {
  index: number;
  fieldId: string;
  rowNumber: number;
  row: PackingListRow;
  deleteDisabled: boolean;
  isMultiStop: boolean;
};

function ContainerTableRow({ index, fieldId, rowNumber, row, deleteDisabled, isMultiStop }: ContainerTableRowProps) {
  const intl = useIntl();
  const { control } = useFormContext();
  const {
    packingListConfig,
    containerTypeOptions,
    weightUnitOptions,
    temperatureOptions,
    dangerousGoodsSelectOptions,
    duplicateRow,
    deleteRow,
    handlePackingListFieldBlur,
    handlingUnitHashMap,
  } = usePackingList();
  const nmfcNumberEnabled = packingListConfig?.nmfc_number_enabled;
  const { hazardousEnabled, temperatureEnabled, dangerousEnabled } = getPackingListWorkflowColumnFlags(packingListConfig);
  const { hasAttemptedReview } = useRequestFormReviewValidation();

  const currentRow = (useWatch({ name: `packing_list.${index}`, control }) as PackingListRow | undefined) ?? row;

  usePackingListRowReactiveClear(index, currentRow, handlingUnitHashMap, nmfcNumberEnabled);
  const requiredHintPaths = getPackingListRequiredHintPaths(
    currentRow,
    index,
    handlingUnitHashMap,
    hasAttemptedReview,
    nmfcNumberEnabled,
  );
  const hasRequiredHint = (field: string) => requiredHintPaths.includes(getPackingListFieldPath(index, field));

  return (
    <PackingListTableRow
      rowNumber={rowNumber}
      onDuplicate={() => duplicateRow(fieldId)}
      onDelete={() => deleteRow(fieldId)}
      deleteDisabled={deleteDisabled}
    >
      <PackingListCell style={{ width: CONTAINER_COLUMN_WIDTHS.containerType }}>
        <PackingListSelect
          name={getPackingListFieldPath(index, "container_type")}
          hideError={hasRequiredHint("container_type")}
          options={containerTypeOptions}
          placeholder={intl.formatMessage({
            id: "request-form.packing-list.selectContainerType",
            defaultMessage: "Select container",
          })}
          onChange={() => handlePackingListFieldBlur(fieldId)}
        />
        {hasRequiredHint("container_type") && <PackingListRequiredHint />}
      </PackingListCell>
      <PackingListCell className="rfv2-packing-list-table__cell--weight">
        <div className="rfv2-packing-list-table__weight-input">
          <Input
            name={getPackingListFieldPath(index, "weight_piece")}
            type="number"
            placeholder="0.00"
            hideError={hasRequiredHint("weight_piece")}
            onChange={() => handlePackingListFieldBlur(fieldId)}
          />
          {hasRequiredHint("weight_piece") && <PackingListRequiredHint />}
        </div>
        <WeightUnitSelect index={index} options={weightUnitOptions} onChange={() => handlePackingListFieldBlur(fieldId)} />
      </PackingListCell>
      <PackingListCell className="rfv2-packing-list-table__cell--qty" style={{ width: CONTAINER_COLUMN_WIDTHS.qty }}>
        <Input
          name={getPackingListFieldPath(index, "units")}
          type="number"
          hideError={hasRequiredHint("units")}
          onChange={() => handlePackingListFieldBlur(fieldId)}
        />
        {hasRequiredHint("units") && <PackingListRequiredHint />}
      </PackingListCell>
      {isMultiStop && (
        <>
          <PackingListCell style={{ width: CONTAINER_COLUMN_WIDTHS.stop }}>
            <PackingListStopSelect index={index} fieldId={fieldId} type="pickup" />
          </PackingListCell>
          <PackingListCell style={{ width: CONTAINER_COLUMN_WIDTHS.stop }}>
            <PackingListStopSelect index={index} fieldId={fieldId} type="delivery" />
          </PackingListCell>
        </>
      )}
      {hazardousEnabled && (
        <PackingListCell className="rfv2-packing-list-table__cell--center" style={{ width: CONTAINER_COLUMN_WIDTHS.toggle }}>
          <Checkbox name={getPackingListFieldPath(index, "hazardous")} />
        </PackingListCell>
      )}
      {temperatureEnabled && (
        <PackingListCell
          className="rfv2-packing-list-table__cell--temperature"
          style={{ width: CONTAINER_COLUMN_WIDTHS.temperature }}
        >
          <TemperatureSelect index={index} options={temperatureOptions} onChange={() => handlePackingListFieldBlur(fieldId)} />
        </PackingListCell>
      )}
      {dangerousEnabled && (
        <PackingListCell
          className="rfv2-packing-list-table__cell--dangerous"
          style={{ width: CONTAINER_COLUMN_WIDTHS.dangerous }}
        >
          <DangerousGoodsSelect
            index={index}
            options={dangerousGoodsSelectOptions}
            onChange={() => handlePackingListFieldBlur(fieldId)}
          />
        </PackingListCell>
      )}
      <PackingListCell style={{ width: CONTAINER_COLUMN_WIDTHS.additional }}>
        <Input
          name={getPackingListFieldPath(index, "additional_information")}
          onChange={() => handlePackingListFieldBlur(fieldId)}
        />
      </PackingListCell>
    </PackingListTableRow>
  );
}

export default memo(ContainerTableRow);
